import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, X, Heart, Info, Sparkles, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getFavorites, getRecentColors, isFavorite, pushRecentColor, toggleFavorite } from "@/lib/studio/favorites";
import { computeRelated, type ColorLite } from "@/lib/studio/related-colors";

export type SelectedColor = {
  id: string;
  brand: string;
  collection: string | null;
  color_number: string;
  color_name: string;
  hex: string;
  red: number;
  green: number;
  blue: number;
  lrv: number | null;
  swatch_url: string | null;
};

type PaintColorRow = {
  id: string;
  color_number: string;
  color_name: string;
  locator_number: string | null;
  category: string | null;
  red: number;
  green: number;
  blue: number;
  hex: string;
  lrv: number | null;
  swatch_url: string | null;
  collection_id: string | null;
  paint_collections: { name: string } | null;
  paint_brands: { name: string } | null;
};

// Larger page = fewer round-trips; combined with content-visibility below,
// off-screen tiles stay cheap to paint even with hundreds of swatches.
const PAGE_SIZE = 240;

// Quick-filter chips. Each maps to a predicate evaluated client-side against the
// loaded page (cheap — page is at most 60 rows). For Popular/Trending we'd wire
// to telemetry later; for now they fall back to curated category matches.
const QUICK_FILTERS = [
  { id: "all", label: "All" },
  { id: "whites", label: "Whites" },
  { id: "off-whites", label: "Off-Whites" },
  { id: "greiges", label: "Greiges" },
  { id: "grays", label: "Grays" },
  { id: "blacks", label: "Blacks" },
  { id: "blues", label: "Blues" },
  { id: "greens", label: "Greens" },
  { id: "reds", label: "Reds" },
  { id: "browns", label: "Browns" },
  { id: "warm", label: "Warm" },
  { id: "cool", label: "Cool" },
] as const;
type QuickFilterId = typeof QUICK_FILTERS[number]["id"];

function matchesQuickFilter(row: PaintColorRow, id: QuickFilterId): boolean {
  if (id === "all") return true;
  const cat = (row.category ?? "").toLowerCase();
  const name = row.color_name.toLowerCase();
  const { red: r, green: g, blue: b } = row;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const chroma = max - min;

  switch (id) {
    case "whites":
      return min > 230 || cat.includes("white");
    case "off-whites":
      return (min > 200 && min <= 230) || cat.includes("off-white") || cat.includes("cream");
    case "greiges":
      return cat.includes("greige") || cat.includes("beige") || (chroma < 25 && r >= 150 && r <= 220);
    case "grays":
      return cat.includes("gray") || cat.includes("grey") || (chroma < 18 && max >= 60 && max <= 200);
    case "blacks":
      return max < 60 || cat.includes("black");
    case "blues":
      return cat.includes("blue") || (b > r && b > g && chroma > 30);
    case "greens":
      return cat.includes("green") || (g > r && g > b && chroma > 30);
    case "reds":
      return cat.includes("red") || cat.includes("orange") || cat.includes("pink") || (r > g && r > b && chroma > 40);
    case "browns":
      return cat.includes("brown") || cat.includes("tan") || (r > b && g > b && chroma > 30 && max < 200);
    case "warm":
      return r > b + 15;
    case "cool":
      return b > r + 5;
    default:
      return name.length > 0;
  }
}

function useDebounced<T>(value: T, ms = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function readableTextOn(hex: string) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 145 ? "#0a0a0a" : "#ffffff";
}

export function ColorPicker({
  onSelect,
}: {
  onSelect?: (c: SelectedColor) => void;
}) {
  const [query, setQuery] = useState("");
  const [collectionId, setCollectionId] = useState<string>("");
  const [category, setCategory] = useState<string>("");
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<PaintColorRow | null>(null);
  const [quick, setQuick] = useState<QuickFilterId>("all");
  const [favTick, setFavTick] = useState(0); // bump to re-read localStorage
  const [recents, setRecents] = useState<SelectedColor[]>(() => getRecentColors());
  const favorites = useMemo(() => { void favTick; return getFavorites(); }, [favTick]);
  const debounced = useDebounced(query, 250);

  // Reset to first page when filters change
  useEffect(() => { setPage(0); }, [debounced, collectionId, category]);

  const collections = useQuery({
    queryKey: ["paint-collections"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("paint_collections")
        .select("id, name")
        .order("name");
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 5 * 60_000,
  });

  const categories = useQuery({
    queryKey: ["paint-categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("paint_colors")
        .select("category")
        .not("category", "is", null)
        .limit(2000);
      if (error) throw error;
      const set = new Set<string>();
      (data ?? []).forEach((r: { category: string | null }) => r.category && set.add(r.category));
      return Array.from(set).sort();
    },
    staleTime: 5 * 60_000,
  });

  const colors = useQuery({
    queryKey: ["paint-colors", debounced, collectionId, category, page],
    queryFn: async () => {
      const from = page * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      let q = supabase
        .from("paint_colors")
        .select(
          "id, color_number, color_name, locator_number, category, red, green, blue, hex, lrv, swatch_url, collection_id, paint_collections(name), paint_brands(name)",
          { count: "exact" },
        )
        .eq("is_active", true)
        .order("color_number")
        .range(from, to);
      if (collectionId) q = q.eq("collection_id", collectionId);
      if (category) q = q.eq("category", category);
      if (debounced.trim()) {
        const term = debounced.trim();
        q = q.or(`color_name.ilike.%${term}%,color_number.ilike.%${term}%,locator_number.ilike.%${term}%,category.ilike.%${term}%`);
      }
      const { data, error, count } = await q;
      if (error) throw error;
      return { rows: (data ?? []) as unknown as PaintColorRow[], total: count ?? 0 };
    },
    placeholderData: (prev) => prev,
  });

  const totalPages = colors.data ? Math.max(1, Math.ceil(colors.data.total / PAGE_SIZE)) : 1;

  const clearFilters = () => {
    setQuery("");
    setCollectionId("");
    setCategory("");
  };

  const rowToSelected = (row: PaintColorRow): SelectedColor => ({
    id: row.id,
    brand: row.paint_brands?.name ?? "Sherwin-Williams",
    collection: row.paint_collections?.name ?? null,
    color_number: row.color_number,
    color_name: row.color_name,
    hex: row.hex,
    red: row.red,
    green: row.green,
    blue: row.blue,
    lrv: row.lrv,
    swatch_url: row.swatch_url,
  });

  const handleSelect = (row: PaintColorRow) => {
    setSelected(row);
    const sc = rowToSelected(row);
    pushRecentColor(sc);
    setRecents(getRecentColors());
    onSelect?.(sc);
  };

  // Allow clicking a favorite/recent chip even when its full row isn't loaded.
  const handleSelectFromCache = (sc: SelectedColor) => {
    pushRecentColor(sc);
    setRecents(getRecentColors());
    onSelect?.(sc);
  };

  const handleToggleFavorite = (sc: SelectedColor) => {
    toggleFavorite(sc);
    setFavTick((n) => n + 1);
  };

  const filteredRows = useMemo(
    () => (colors.data?.rows ?? []).filter((r) => matchesQuickFilter(r, quick)),
    [colors.data, quick],
  );

  const mapLiteToSelected = (c: ColorLite): SelectedColor => ({
    id: c.id, brand: c.brand, collection: c.collection,
    color_number: c.color_number, color_name: c.color_name,
    hex: c.hex, red: c.red, green: c.green, blue: c.blue,
    lrv: c.lrv, swatch_url: c.swatch_url,
  });

  // ---- Related (Similar / Coordinating / Variations) ----
  // Fetch a candidate pool once per hue neighborhood; recompute related lists client-side.
  const relatedPool = useQuery({
    queryKey: ["paint-colors-related-pool", selected?.category ?? "all"],
    enabled: !!selected,
    queryFn: async () => {
      let q = supabase
        .from("paint_colors")
        .select("id, color_number, color_name, category, red, green, blue, hex, lrv, swatch_url, paint_collections(name), paint_brands(name)")
        .eq("is_active", true)
        .limit(600);
      if (selected?.category) q = q.eq("category", selected.category);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as PaintColorRow[];
    },
    staleTime: 5 * 60_000,
  });

  const related = useMemo(() => {
    if (!selected || !relatedPool.data) return null;
    const seed: ColorLite = {
      id: selected.id, brand: selected.paint_brands?.name ?? "Sherwin-Williams",
      collection: selected.paint_collections?.name ?? null,
      color_number: selected.color_number, color_name: selected.color_name,
      hex: selected.hex, red: selected.red, green: selected.green, blue: selected.blue,
      lrv: selected.lrv, swatch_url: selected.swatch_url,
    };
    const pool: ColorLite[] = relatedPool.data.map((r) => ({
      id: r.id, brand: r.paint_brands?.name ?? "Sherwin-Williams",
      collection: r.paint_collections?.name ?? null,
      color_number: r.color_number, color_name: r.color_name,
      hex: r.hex, red: r.red, green: r.green, blue: r.blue,
      lrv: r.lrv, swatch_url: r.swatch_url,
    }));
    return computeRelated(seed, pool);
  }, [selected, relatedPool.data]);

  const activeFilters = useMemo(
    () => [debounced && `"${debounced}"`, collectionId && collections.data?.find((c) => c.id === collectionId)?.name, category].filter(Boolean),
    [debounced, collectionId, category, collections.data],
  );

  return (
    <div className="grid lg:grid-cols-[1fr,360px] gap-8">
      <div>
        {/* Search + filter bar */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-4 mb-6">
          <div className="flex items-center gap-3 px-3 py-2 rounded-xl bg-black/40 border border-white/10 focus-within:border-amber-300/40 transition-colors">
            <Search className="size-5 text-zinc-400 shrink-0" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, SW number, locator, or category…"
              className="bg-transparent outline-none text-white placeholder:text-zinc-500 text-base flex-1 min-w-0"
              aria-label="Search paint colors"
            />
            {query && (
              <button onClick={() => setQuery("")} className="text-zinc-400 hover:text-white" aria-label="Clear search">
                <X className="size-4" />
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <select
              value={collectionId}
              onChange={(e) => setCollectionId(e.target.value)}
              className="bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white outline-none focus:border-amber-300/40"
              aria-label="Filter by collection"
            >
              <option value="">All collections</option>
              {collections.data?.map((c) => (
                <option key={c.id} value={c.id} className="bg-zinc-900">{c.name}</option>
              ))}
            </select>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white outline-none focus:border-amber-300/40"
              aria-label="Filter by color family"
            >
              <option value="">All color families</option>
              {categories.data?.map((c) => (
                <option key={c} value={c} className="bg-zinc-900">{c}</option>
              ))}
            </select>
          </div>
          {activeFilters.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap mt-3">
              {activeFilters.map((f, i) => (
                <span key={i} className="text-[11px] uppercase tracking-wider text-amber-200 bg-amber-500/10 border border-amber-300/20 rounded-full px-2.5 py-1">{f}</span>
              ))}
              <button onClick={clearFilters} className="text-[11px] text-zinc-400 hover:text-white underline underline-offset-2">Clear all</button>
            </div>
          )}

          {/* Quick-family chips */}
          <div className="flex items-center gap-1.5 flex-wrap mt-3">
            {QUICK_FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setQuick(f.id)}
                className={`text-[11px] px-2.5 py-1 rounded-full border transition ${
                  quick === f.id
                    ? "border-amber-300 bg-amber-300 text-zinc-950 font-semibold"
                    : "border-white/10 text-zinc-300 hover:border-white/30"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Favorites + Recents strip */}
        {(favorites.length > 0 || recents.length > 0) && (
          <div className="space-y-3 mb-5">
            {favorites.length > 0 && (
              <ColorStrip
                icon={<Heart className="size-3 fill-current" />}
                title="Favorites"
                colors={favorites}
                onPick={handleSelectFromCache}
              />
            )}
            {recents.length > 0 && (
              <ColorStrip
                icon={<Clock className="size-3" />}
                title="Recently viewed"
                colors={recents}
                onPick={handleSelectFromCache}
              />
            )}
          </div>
        )}

        {/* Related strips — appear once a color is picked */}
        {selected && related && (
          <div className="space-y-3 mb-5">
            {related.similar.length > 0 && (
              <ColorStrip
                icon={<Sparkles className="size-3" />}
                title="Similar colors"
                colors={related.similar.map(mapLiteToSelected)}
                onPick={handleSelectFromCache}
              />
            )}
            {related.coordinating.length > 0 && (
              <ColorStrip
                icon={<Sparkles className="size-3" />}
                title="Coordinating colors"
                colors={related.coordinating.map(mapLiteToSelected)}
                onPick={handleSelectFromCache}
              />
            )}
            {related.variations.length > 0 && (
              <ColorStrip
                icon={<Sparkles className="size-3" />}
                title="Lighter · darker variations"
                colors={related.variations.map(mapLiteToSelected)}
                onPick={handleSelectFromCache}
              />
            )}
          </div>
        )}

        {/* Disclaimer */}
        <div className="flex items-start gap-2 text-[11px] text-zinc-500 mb-4 px-1">
          <Info className="size-3.5 mt-0.5 shrink-0 text-amber-200/70" />
          <p>Digital paint colors are approximate and can vary by screen, lighting, camera, and surface texture. Always verify with a physical paint sample before purchasing paint.</p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
          {colors.isLoading && Array.from({ length: 24 }).map((_, i) => (
            <div key={i} className="aspect-square rounded-xl bg-white/5 animate-pulse" />
          ))}
          {!colors.isLoading && filteredRows.length === 0 && (
            <div className="col-span-full text-center py-16 text-zinc-500">No colors match these filters.</div>
          )}
          {filteredRows.map((row) => {
            const isSelected = selected?.id === row.id;
            const textColor = readableTextOn(row.hex);
            const fav = isFavorite(row.id);
            return (
              <button
                key={row.id}
                onClick={() => handleSelect(row)}
                className={`group relative aspect-square rounded-xl overflow-hidden border transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_10px_40px_-10px_rgba(0,0,0,0.6)] ${isSelected ? "border-amber-300 ring-2 ring-amber-300/40" : "border-white/10 hover:border-white/30"}`}
                style={{
                  backgroundColor: row.hex,
                  // Cheap virtualization: browser skips paint/layout for off-screen tiles.
                  contentVisibility: "auto" as unknown as undefined,
                  containIntrinsicSize: "96px 96px" as unknown as undefined,
                }}
                title={`${row.color_name} · ${row.color_number}`}
              >
                {row.swatch_url && (
                  <img src={row.swatch_url} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                )}
                <span
                  onClick={(e) => { e.stopPropagation(); handleToggleFavorite(rowToSelected(row)); }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); handleToggleFavorite(rowToSelected(row)); } }}
                  className={`absolute top-1.5 right-1.5 p-1 rounded-full transition ${fav ? "bg-white/90 text-rose-500 opacity-100" : "bg-black/50 text-white opacity-0 group-hover:opacity-100 hover:bg-black/80"}`}
                  aria-label={fav ? "Remove from favorites" : "Add to favorites"}
                >
                  <Heart className={`size-3 ${fav ? "fill-current" : ""}`} />
                </span>
                <div
                  className="absolute inset-x-0 bottom-0 px-2 py-1.5 text-[10px] font-medium tracking-wide opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ color: textColor, background: `linear-gradient(to top, ${textColor === "#ffffff" ? "rgba(0,0,0,0.55)" : "rgba(255,255,255,0.55)"}, transparent)` }}
                >
                  <div className="truncate font-semibold">{row.color_name}</div>
                  <div className="opacity-70">{row.color_number}</div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Pagination */}
        {colors.data && colors.data.total > PAGE_SIZE && (
          <div className="flex items-center justify-between mt-6 text-sm text-zinc-400">
            <span>
              {(page * PAGE_SIZE + 1).toLocaleString()}–{Math.min((page + 1) * PAGE_SIZE, colors.data.total).toLocaleString()} of {colors.data.total.toLocaleString()}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-1.5 rounded-full border border-white/10 hover:border-white/30 disabled:opacity-40 disabled:cursor-not-allowed"
              >Previous</button>
              <span className="text-xs text-zinc-500">Page {page + 1} / {totalPages}</span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page + 1 >= totalPages}
                className="px-3 py-1.5 rounded-full border border-white/10 hover:border-white/30 disabled:opacity-40 disabled:cursor-not-allowed"
              >Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Detail panel */}
      <aside className="lg:sticky lg:top-24 self-start rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-5 h-fit">
        {!selected ? (
          <div className="text-center py-10">
            <Sparkles className="size-7 text-amber-200 mx-auto mb-3" />
            <p className="font-serif text-xl text-white mb-1">Select a color</p>
            <p className="text-xs text-zinc-500 leading-relaxed">Tap any swatch to preview it here. This panel will feed the AI Paint Studio visualizer.</p>
          </div>
        ) : (
          <>
            <div
              className="rounded-xl h-44 w-full mb-4 border border-white/10 overflow-hidden relative"
              style={{ backgroundColor: selected.hex }}
            >
              {selected.swatch_url && (
                <img src={selected.swatch_url} alt="" className="w-full h-full object-cover" />
              )}
            </div>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-amber-200 mb-1">{selected.paint_brands?.name ?? "Sherwin-Williams"}</p>
                <h3 className="font-serif text-2xl text-white leading-tight">{selected.color_name}</h3>
                <p className="text-xs text-zinc-500 mt-1">{selected.color_number}{selected.locator_number ? ` · Locator ${selected.locator_number}` : ""}</p>
              </div>
              <button
                onClick={() => handleToggleFavorite(rowToSelected(selected))}
                className={`transition-colors p-1.5 rounded-full border border-white/10 ${
                  isFavorite(selected.id)
                    ? "text-rose-400 border-rose-400/40 bg-rose-400/10"
                    : "text-zinc-400 hover:text-amber-300"
                }`}
                aria-label={isFavorite(selected.id) ? "Remove favorite" : "Save favorite"}
              >
                <Heart className={`size-4 ${isFavorite(selected.id) ? "fill-current" : ""}`} />
              </button>
            </div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
              <dt className="text-zinc-500">HEX</dt>
              <dd className="text-white font-mono">{selected.hex}</dd>
              <dt className="text-zinc-500">RGB</dt>
              <dd className="text-white font-mono">{selected.red}, {selected.green}, {selected.blue}</dd>
              {selected.lrv !== null && (
                <>
                  <dt className="text-zinc-500">LRV</dt>
                  <dd className="text-white">{selected.lrv}</dd>
                </>
              )}
              {selected.category && (
                <>
                  <dt className="text-zinc-500">Family</dt>
                  <dd className="text-white">{selected.category}</dd>
                </>
              )}
              {selected.paint_collections?.name && (
                <>
                  <dt className="text-zinc-500">Collection</dt>
                  <dd className="text-white">{selected.paint_collections.name}</dd>
                </>
              )}
            </dl>
            <div className="mt-5 pt-5 border-t border-white/5">
              <a
                href="/contact"
                className="block text-center bg-white text-zinc-950 px-4 py-2.5 rounded-full text-sm font-semibold hover:bg-amber-300 hover:text-white transition-all"
              >
                Use this color in a free estimate
              </a>
              <p className="text-[10px] text-zinc-500 mt-3 leading-relaxed text-center">
                Digital preview only — closest match on screen. Always confirm with a physical sample.
              </p>
            </div>
          </>
        )}
      </aside>
    </div>
  );
}

function ColorStrip({
  icon,
  title,
  colors,
  onPick,
}: {
  icon: React.ReactNode;
  title: string;
  colors: SelectedColor[];
  onPick: (c: SelectedColor) => void;
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-400 mb-2">
        {icon} {title} <span className="text-zinc-400">· {colors.length}</span>
      </div>
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {colors.map((c) => (
          <button
            key={c.id}
            onClick={() => onPick(c)}
            className="flex-none size-10 rounded-md border border-white/10 hover:border-amber-300 hover:scale-110 transition"
            style={{ background: c.hex }}
            title={`${c.color_name} · ${c.color_number}`}
            aria-label={`${title}: ${c.color_name}`}
          />
        ))}
      </div>
    </div>
  );
}